import { useOktaAuth } from "@okta/okta-react";
import { AxiosError } from "axios";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import dealerApi from "../../api/dealer/DealerApi";
import useDealerApi from "../../api/dealer/useDealerApi";
import { useAccessContext } from "../../context/AccessContext";
import { useErrorStatus } from "../../context/AuthorizationContext";
import { ConfigContext } from "../../context/ConfigContext";
import {
  DelegateContext,
  useEffectiveUserId,
} from "../../context/DelegateContext/DelegateContext";

interface UseApiHookProps<T> {
  apiFn?: (token: string, lang: string) => Promise<T>;
  authorizationEnabled?: boolean;
  accountId?: string;
  multipleAccountsSelected?: boolean;
  doNotAllowMultipleAccounts?: boolean;
}

const useQuery = <T>({
  apiFn,
  authorizationEnabled,
  accountId,
  multipleAccountsSelected = false,
  doNotAllowMultipleAccounts = false,
}: UseApiHookProps<T>) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<T>();
  const [error, setError] = useState<boolean>(false);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const { authState } = useOktaAuth();
  const { setErrorStatusCode } = useErrorStatus();
  const { setHasAccess } = useAccessContext();
  const { i18n } = useTranslation();
  const {
    DEALER_SERVICE_URL,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE,
    ENABLE_RBAC_AUTH_SUPPORT,
  } = useContext(ConfigContext);
  const isAuthorizationEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE;
  const dealerApiHook = useDealerApi();
  const effectiveUserId = useEffectiveUserId();
  const { isDelegating } = useContext(DelegateContext);
  const authorizeAndFetchData = (): void => {
    if (authorizationEnabled && accountId) {
      if (authState?.accessToken && isAuthorizationEnabled) {
        dealerApiHook
          .accountMappedToUser(DEALER_SERVICE_URL, effectiveUserId, accountId)
          .then((authorized: boolean) => {
            if (!authorized) {
              handleNoAccess();
            } else {
              fetchData();
            }
          })
          .catch(handleNoAccess);
        return;
      }
      if (authState?.accessToken) {
        dealerApi
          .accountMappedToUser(
            DEALER_SERVICE_URL,
            effectiveUserId,
            accountId,
            authState?.accessToken.accessToken,
            !isDelegating
          )
          .then((authorized: boolean) => {
            if (!authorized) {
              handleNoAccess();
            } else {
              fetchData();
            }
          })
          .catch(handleNoAccess);
      }
    } else {
      fetchData();
    }
  };

  const handleNoAccess = () => {
    setHasAccess(false);
    setError(true);
    setLoading(false);
  };

  const fetchData = () => {
    if (apiFn && authState?.accessToken) {
      apiFn(authState.accessToken.accessToken, i18n.language)
        .then((response: any) => setData(response))
        .catch((apiError: AxiosError) => {
          if (apiError.response) {
            if (apiError.response?.status === 403) {
              setForbidden(true);
            }

            setErrorStatusCode(apiError.response?.status);
            setError(true);
          }
        })
        .finally(() => setLoading(false));
    }
  };

  const reload = () => {
    setLoading(true);
    setError(false);
    setForbidden(false);
    authorizeAndFetchData();
  };

  useEffect(() => {
    if (doNotAllowMultipleAccounts && multipleAccountsSelected) {
      return;
    }
    authorizeAndFetchData();
  }, []);

  return { loading, data, error, forbidden, reload };
};

export default useQuery;
